/*
 * <fleet-admin-app> — the thin host over the shared <vitesse-shell> (base-ui). It owns
 * fleet DATA (one ListFleet probe) + routing: the left tree is a flat list of VIEWS
 * (Overview · Map · Alerts · Releases · Rollouts), the center renders the active view,
 * and clicking a gateway anywhere (card, map marker, alert, location row) opens the
 * right-side detail drawer. The shell's header/tree/AI-rail + the account-menu/org-switcher
 * widgets (slotted) are all reused unchanged.
 *
 * LIGHT DOM (createRenderRoot → this) so the global base-ui SCSS reaches the views,
 * cards, and panel — same reason as the gateway app's <vitesse-app>.
 */
import { LitElement, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { Transport } from '@connectrpc/connect';
// app-shell surface via the subpath (registers <vitesse-shell> + header/tree/icon).
import '@vitesse/base-ui/elements/vitesse-shell';
// Registers <vitesse-ai-panel>. The rail is the SHARED one — same element, same manager, same
// transcript as the gateway app and the console; only `platform: 'fleet'` differs, which is what
// selects this surface's tools and persona server-side.
import '@vitesse/base-ui/ai';
import { AiManager, configureAiPlane, implementDomPlatformTools, registerGatewayTools, type AiClient } from '@vitesse/base-ui/ai';
import { AI_URL } from '../core/env';
import '@vitesse/base-ui/elements/vitesse-icon';
// The gated-UI overlay + its two intents. The overlay reads the SAME fleet_manager
// requirement the fleet-control handlers enforce, so the lock and the eventual
// refusal cannot disagree; it EMITS the intent and this host routes it (one place),
// mirroring app-ui's app-shell.
import '@vitesse/base-ui/elements/vitesse-gate-overlay';
import { openOrgBilling, getSession, type ClerkSwitcherHost } from '@vitesse/base-ui';
import { requestAccess } from '../core/fleet-client';
import type { ShellTreeNode } from '@vitesse/base-ui/elements/vitesse-nav-tree';
import { type CloudServer, type FleetGateway } from '../core/fleet-client';
import { FleetManager } from '../core/fleet-manager';
import { AlertsManager } from '../core/alerts-manager';
import { isDemoMode, demoFleet } from '../core/demo-fleet';
import { ft, LocaleController } from '../core/i18n';
import '../views/overview'; // registers <fleet-overview>
import { mapView, rolloutsView, releasesView } from '../views/views';
import '../components/alerts-page';
import '../components/gateway-panel'; // registers <fleet-gateway-panel>
import '../components/server-panel'; // registers <fleet-server-panel>

type ViewKey = 'overview' | 'releases' | 'map' | 'alerts' | 'rollouts';

type LoadState = { phase: 'loading' } | { phase: 'ready'; gateways: FleetGateway[] } | { phase: 'error'; message: string };

@customElement('fleet-admin-app')
export class FleetAdminApp extends LitElement {
  /** The FleetControl Connect transport (wired with the session token in main/index.ts). */
  @property({ attribute: false }) transport!: Transport;

  /** The AI plane handle (its worker), created during boot. The manager is built from it here. */
  @property({ attribute: false }) ai?: AiClient;

  /** The session token, as a function. Passed in rather than reaching for the IdP client: this app
   *  needs one capability, not the whole client, and the AI plane re-reads it per call so a
   *  refreshed session is picked up without rewiring. */
  @property({ attribute: false }) aiToken: () => Promise<string | null> = async () => null;
  /** Account-menu / org-switcher light-DOM nodes (mounted in main/index.ts). */
  @property({ attribute: false }) userEl?: HTMLElement;
  @property({ attribute: false }) orgEl?: HTMLElement;
  /** First name for the greeting. */
  @property() userName = '';
  /** orgId → human name, threaded to the overview + detail panel. */
  @property({ attribute: false }) orgNames: Map<string, string> = new Map();
  /** Vitesse super-admin (server-verified). UX-only — gates the dev ?demo aid. */
  @property({ type: Boolean }) superAdmin = false;
  /** The Clerk identity singleton — used ONLY to open the org-dialog Billing page
   *  when a gated surface's Upgrade button is pressed (it satisfies ClerkSwitcherHost
   *  structurally). Set in main/index.ts; upgrade no-ops safely if absent. */
  @property({ attribute: false }) identity?: ClerkSwitcherHost;

  @state() private load: LoadState = { phase: 'loading' };
  @state() private view: ViewKey = 'overview';
  @state() private selected: FleetGateway | null = null;
  /** Open server detail panel (cloud hub clicked on the map); null → closed. */
  @state() private selectedServer: CloudServer | null = null;
  /** Map module loaded? (maplibre + deck.gl are lazy-loaded on first Map-view open.) */
  @state() private mapReady = false;
  @state() private aiManager?: AiManager;
  /** Unregisters the DOM platform tools, so a torn-down shell does not leave them answering. */
  private aiToolsOff?: () => void;
  private aiGatewayToolsOff?: () => void;
  /** The handshake is once-only, and only on the first rail-open. */
  private aiStarted = false;
  private manager?: FleetManager;
  /** Owns the fleet audit-log + alerts feed — backs the Alerts view + the header bell. */
  private alerts?: AlertsManager;
  /** DEV-only synthetic fleet — `?demo` AND super-admin only (so the dev aid never
   *  shows cross-org synthetic data to a customer org-admin). Memoized on first use. */
  private _demo?: ReturnType<typeof demoFleet> | null;
  private get demo(): ReturnType<typeof demoFleet> | null {
    if (this._demo === undefined) this._demo = isDemoMode() && this.superAdmin ? demoFleet() : null;
    return this._demo;
  }

  constructor() {
    super();
    new LocaleController(this); // re-render tree/AI-rail/messages on language switch
  }

  // Light DOM — the global base-ui SCSS styles the views/cards/panel.
  protected createRenderRoot(): HTMLElement {
    return this;
  }

  /**
   * Build the AI manager as soon as the plane handle arrives — cheap, and it pulls the live model
   * catalog on construction so the picker is populated before anyone opens the rail.
   *
   * `configureAiPlane` is the one plane-wiring call (driver + live channel). Omitting it leaves the
   * surface deaf to config changes: a tool gated in the console keeps running ungated here until
   * the rail is reopened.
   */
  protected willUpdate(changed: Map<string, unknown>): void {
    if (changed.has('ai') && this.ai && !this.aiManager) {
      const token = (): Promise<string | null> => this.aiToken();
      configureAiPlane(AI_URL, token, 'fleet');
      // The screen tools are pure DOM and the same in every app; without this call the assistant
      // here cannot see what the user is looking at, and — because an unimplemented tool is never
      // offered — says nothing about why.
      this.aiToolsOff = implementDomPlatformTools();
      /*
       * …AND THE ROSTER, which this surface is entirely about.
       *
       * The fleet dashboard advertises four questions — are any relays offline, how is the fleet's
       * health, which need an update, where are they — and shipped with no tool that could answer
       * one of them. Its assistant had the three DOM tools and nothing else, so asked "how many
       * relays are online" it correctly said it could not read that, and every suggested prompt on
       * the page was dead on arrival.
       *
       * The same tool app-ui uses, on the same record: this manager's list IS the fleet registry's
       * rows, which is what `gateways.list` has always returned. Registered from the manager rather
       * than a fresh RPC so the assistant can never see a relay the person on the page cannot.
       */
      this.aiGatewayToolsOff = registerGatewayTools({ entries: () => this.gateways.map((gateway) => ({ gateway })) });
      this.aiManager = new AiManager(this.ai, 'fleet', { aiUrl: AI_URL, token });
    }
  }

  /**
   * First rail-open mints the ephemeral token. Deferred rather than done at boot, so a session that
   * never opens the rail never pays for it. Once-only: the shell re-announces on every open.
   */
  private onAiOpen = (): void => {
    if (!this.aiManager || this.aiStarted) return;
    this.aiStarted = true;
    void this.aiManager.start();
  };

  connectedCallback(): void {
    super.connectedCallback();
    // The gated-UI overlays (Map view, Alerts view, and the gateway panel's
    // Hardware/Network tabs) bubble their two intents to this host — ONE place
    // routes them, so every gated surface behaves identically. Request-access
    // reaches the org's admins via fleet-control; upgrade opens the org Billing page.
    this.addEventListener('vitesse-gate-request-access', this.onGateRequestAccess as EventListener);
    this.addEventListener('vitesse-gate-upgrade', this.onGateUpgrade);
  }

  /** A gated surface's Request-access button was pressed — carry the ask to the
   *  org's admins. The overlay already flipped to "sent" optimistically, so only a
   *  real failure needs surfacing (fleet-ui has no toast surface — console). */
  private readonly onGateRequestAccess = (e: CustomEvent<{ permission: string; context: string }>): void => {
    if (!this.transport) return;
    void requestAccess(this.transport, e.detail.permission, e.detail.context).catch((err: unknown) =>
      console.error('[fleet] request access failed', err),
    );
  };

  /** A gated surface's Upgrade button — open the org-dialog Billing page. The
   *  identity IS the Clerk singleton; openOrgBilling no-ops safely if the modal
   *  API is absent. */
  private readonly onGateUpgrade = (): void => {
    if (this.identity) openOrgBilling(this.identity);
  };

  firstUpdated(): void {
    // NO POLLING — one FleetManager owns the live SubscribeFleet stream + the
    // canonical fleet state (app-ui manager pattern); the app just reacts
    // to its `change` event (snapshot → ~1Hz deltas). Reconnect-forever inside.
    if (!this.transport) {
      this.load = { phase: 'error', message: 'no transport' };
      return;
    }
    const m = new FleetManager(this.transport);
    this.manager = m;
    m.addEventListener('change', () => this.onManagerChange());
    m.start();
    // The alerts/audit feed — same manager pattern, its own SubscribeEvents stream.
    // Feeds the header bell (.notifications below) AND the Alerts view; a `change`
    // just re-renders (the bell self-updates off the same manager).
    const a = new AlertsManager(this.transport);
    this.alerts = a;
    a.addEventListener('change', () => this.requestUpdate());
    a.start();

    // Warm the heavy map chunk (maplibre + deck.gl) during idle, so the first
    // Map-view open paints instantly instead of waiting on the dynamic import.
    // loadMap() is idempotent; the idle callback keeps it off the boot critical
    // path. (rIC where available, else a short timer.)
    // Only warm the heavy map chunk for a reader whose plan can actually open the
    // Map view (fleet_manager "full") — a denied reader never pulls it (same guard
    // as the on-open load in renderCenter).
    const warmMap = () => {
      if (getSession()?.allows({ capability: 'fleet_manager', level: 'full' }) !== false) void this.loadMap();
    };
    const ric = (
      window as unknown as {
        requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => void;
      }
    ).requestIdleCallback;
    if (ric) ric(warmMap, { timeout: 3000 });
    else window.setTimeout(warmMap, 1200);
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.removeEventListener('vitesse-gate-request-access', this.onGateRequestAccess as EventListener);
    this.removeEventListener('vitesse-gate-upgrade', this.onGateUpgrade);
    this.aiToolsOff?.();
    this.aiGatewayToolsOff?.();
    this.manager?.stop();
    this.alerts?.stop();
  }

  /** React to a fleet stream frame: swap in the live data + keep an open panel's
   *  record current (the panel's fingerprint guard means this never resets its
   *  tab/dialog). A transient stream error keeps the last-known fleet on screen. */
  private onManagerChange(): void {
    const m = this.manager;
    if (!m) return;
    if (m.ready) {
      this.load = { phase: 'ready', gateways: m.list() };
      // Re-resolve an open panel against the MERGED list (real + demo) so a
      // synthetic selection survives the live refresh too.
      if (this.selected) {
        const fp = this.selected.deviceFingerprint;
        this.selected = this.gateways.find((g) => g.deviceFingerprint === fp) ?? null;
      }
      // Keep an open SERVER panel live too, so its sparklines accumulate from the stream.
      if (this.selectedServer) {
        const id = this.selectedServer.id;
        this.selectedServer = this.cloudServers.find((c) => c.id === id) ?? null;
      }
    } else if (m.error && this.load.phase !== 'ready') {
      this.load = { phase: 'error', message: m.error };
    }
  }

  private retry(): void {
    this.load = { phase: 'loading' };
    this.manager?.refresh();
  }

  private get gateways(): FleetGateway[] {
    const real = this.load.phase === 'ready' ? this.load.gateways : [];
    return this.demo ? [...real, ...this.demo.gateways] : real;
  }

  /** orgNames merged with the demo orgs (when ?demo) so synthetic boxes group + label. */
  private get effectiveOrgNames(): Map<string, string> {
    return this.demo ? new Map([...this.orgNames, ...this.demo.orgNames]) : this.orgNames;
  }

  /** Private cloud servers — the demo set when ?demo, else the REAL ones the
   *  FleetManager decodes from the SubscribeFleet `cloud_servers` stream field. */
  private get cloudServers(): CloudServer[] {
    return this.demo?.cloudServers ?? this.manager?.cloudServers() ?? [];
  }

  /** The left rail is a flat list of VIEWS (same tree styling, view items not a
   *  component hierarchy). Alerts carries a live count of things needing attention. */
  private tree(): ShellTreeNode[] {
    return [
      { key: 'overview', label: ft('tree.overview'), icon: 'house' },
      { key: 'map', label: ft('tree.map'), icon: 'map' },
      { key: 'alerts', label: ft('tree.alerts'), icon: 'bell' },
      { key: 'releases', label: ft('tree.releases'), icon: 'package' },
      { key: 'rollouts', label: ft('tree.rollouts'), icon: 'rocket' },
    ];
  }

  private onNodeSelect(e: Event): void {
    this.view = (e as CustomEvent<{ key: string }>).detail.key as ViewKey;
  }

  private onGatewayOpen(e: Event): void {
    const fp = (e as CustomEvent<{ fingerprint: string }>).detail.fingerprint;
    this.selectedServer = null; // one drawer at a time
    this.selected = this.gateways.find((g) => g.deviceFingerprint === fp) ?? null;
  }

  /** Map cloud-hub marker clicked → open the server detail panel (closes any gateway panel). */
  private onCloudOpen(e: Event): void {
    const id = (e as CustomEvent<{ id: string }>).detail.id;
    this.selected = null;
    this.selectedServer = this.cloudServers.find((c) => c.id === id) ?? null;
  }

  private onPanelClose(): void {
    this.selected = null;
    this.selectedServer = null;
  }

  private onFleetChanged(): void {
    this.selected = null;
    this.manager?.refresh(); // force a clean snapshot (a removed gateway must vanish)
  }

  private mapLoading = false;
  /** Lazy-load the map module (maplibre + deck.gl) on first Map-view open — keeps that
   *  heavy stack out of the app-shell bundle + the initial dep-optimize. */
  private async loadMap(): Promise<void> {
    if (this.mapReady || this.mapLoading) return;
    this.mapLoading = true;
    await import('@vitesse/base-ui/elements/vitesse-world-map');
    this.mapReady = true;
  }

  private renderCenter(): TemplateResult {
    if (this.load.phase === 'loading') return html`<div class="fleet-msg">${ft('msg.loading')}</div>`;
    if (this.load.phase === 'error')
      return html`<div class="fleet-msg err">
        ${ft('msg.listFailed', { message: this.load.message })}
        <button class="base-button base-button-blue" @click=${this.retry} title=${ft('hint.refresh')}>${ft('btn.retry')}</button>
      </div>`;
    switch (this.view) {
      case 'overview':
        return html`<fleet-overview
          .gateways=${this.gateways}
          userName=${this.userName}
          .orgNames=${this.effectiveOrgNames}
        ></fleet-overview>`;
      case 'map': {
        // Map is the top tier — fleet_manager level "full". GUARD the parent-driven
        // load: a denied reader must not pull the heavy maplibre + deck.gl chunk for
        // a view the overlay will only ever paint as a skeleton. When allowed but not
        // yet loaded, kick the import and show the loading line; the overlay renders
        // whichever `.content` is current and re-renders as it resolves.
        const mayMap = getSession()?.allows({ capability: 'fleet_manager', level: 'full' }) !== false;
        if (mayMap && !this.mapReady) void this.loadMap();
        const mapContent =
          this.mapReady && mayMap
            ? mapView(this.gateways, this.effectiveOrgNames, this.cloudServers, this.selected?.deviceFingerprint ?? '')
            : html`<div class="fleet-msg">${ft('msg.loading')}</div>`;
        return html`<vitesse-gate-overlay
          class="fleet-gate-fill"
          capability="fleet_manager"
          level="full"
          context="map"
          .content=${mapContent}
        ></vitesse-gate-overlay>`;
      }
      case 'alerts':
        // Alerts is fleet_manager level "standard" (pro). The AlertsManager stream is
        // app-level (it also feeds the header bell), so there is no view-scoped fetch
        // to guard — the overlay simply blurs the page when the plan doesn't reach it.
        return html`<vitesse-gate-overlay
          capability="fleet_manager"
          level="standard"
          context="alerts"
          .content=${html`<fleet-alerts-page
            .items=${this.alerts?.events() ?? []}
            error=${this.alerts?.error ?? ''}
          ></fleet-alerts-page>`}
        ></vitesse-gate-overlay>`;
      case 'rollouts':
        return rolloutsView(this.gateways);
      case 'releases':
        return releasesView(this.gateways);
    }
  }

  render(): TemplateResult {
    return html`
      <vitesse-shell
        .treeNodes=${this.tree()}
        .notifications=${this.alerts}
        selectedKey=${this.view}
        @node-select=${this.onNodeSelect}
        @vitesse-home=${() => (this.view = 'overview')}
        @gateway-open=${this.onGatewayOpen}
        @cloud-open=${this.onCloudOpen}
        @panel-close=${this.onPanelClose}
        @fleet-changed=${this.onFleetChanged}
        @vitesse-ai-open=${this.onAiOpen}
      >
        ${this.orgEl}${this.userEl}
        <vitesse-ai-panel slot="ai" .manager=${this.aiManager} userName=${this.userName}></vitesse-ai-panel>
        <div class="fleet-center">${this.renderCenter()}</div>
        ${
          this.selected
            ? html`<fleet-gateway-panel
                .gateway=${this.selected}
                .transport=${this.transport}
                .orgNames=${this.effectiveOrgNames}
              ></fleet-gateway-panel>`
            : nothing
        }
        ${this.selectedServer ? html`<fleet-server-panel .server=${this.selectedServer}></fleet-server-panel>` : nothing}
      </vitesse-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fleet-admin-app': FleetAdminApp;
  }
}
